/**
 * Copyright (c) 2026 Stefan Radig
 *
 * The Android app's location bridge (EditoorLocation, see nativeBridge.ts).
 * Methods: getPermissionStatus, requestPermission, startTracking(intervalMs),
 * stopTracking, getCurrentLocation, getDebugLogs, clearDebugLogs. Results of
 * requestPermission and getCurrentLocation arrive later as the window events
 * `locationPermissionResult` / `currentLocationResult`, fixes as
 * `backgroundLocation`; the app sends them only to a trusted server's page, and
 * fixes only after the page said `ready` (listenForBackgroundLocation).
 */
import { hasNativeObject, nativeRequest } from './nativeBridge';
import { isNativeRefusal, logNativeRefusal } from './nativeAvailability';
import { DebugLogger } from './debugLogger';
import type { GeoPosition, LocationStorage } from './locationStorage';

export type AndroidLocationReply = { ok: true; result: unknown } | { ok: false; refused: boolean };

/** True inside the Android app (whether this server may use it is up to the app). */
export function hasAndroidLocation(): boolean {
    return hasNativeObject('EditoorLocation');
}

/**
 * One request to the location bridge. `refused` means the app does not give this
 * server location access: callers treat that as "location not supported here".
 */
export async function androidLocation(method: string, args: unknown[] = []): Promise<AndroidLocationReply> {
    try {
        return { ok: true, result: await nativeRequest('EditoorLocation', method, args) };
    } catch (error) {
        const refused = isNativeRefusal(error);
        if (refused) logNativeRefusal('location');
        return { ok: false, refused };
    }
}

export interface BackgroundLocationDetail {
    latitude: number;
    longitude: number;
    accuracy: number;
    timestamp?: number;
}

/**
 * Listens for the app's GPS fixes, then tells the app this page is ready for them
 * (once per page). The app keeps fixes queued until then, and counts a fix as taken
 * only when this listener cancels its event (preventDefault); what the page does not
 * take stays queued for the next ready page.
 */
export function listenForBackgroundLocation(onFix: (detail: BackgroundLocationDetail) => void): void {
    window.addEventListener('backgroundLocation', ((event: CustomEvent<BackgroundLocationDetail>) => {
        event.preventDefault();
        onFix(event.detail);
    }) as EventListener);
    if (hasAndroidLocation()) void androidLocation('ready');
}

/**
 * Stores a fix from the app unless it is already stored, and logs it. The app may
 * deliver a fix again (a page change cut off a batch's acknowledgement): such a
 * duplicate gets one log line and is not passed on (false).
 */
export async function storeBackgroundFix(
    storage: Pick<LocationStorage, 'storeIfNew'>,
    pos: GeoPosition,
    subscriptions: number
): Promise<boolean> {
    if (!(await storage.storeIfNew(pos))) {
        DebugLogger.log('receive', 'Location fix already stored, ignored');
        return false;
    }
    // delayMs is how long the fix took to reach JS: large values mean the
    // WebView was suspended (screen off) and the fix was delivered late.
    DebugLogger.log('receive', 'Location fix', {
        delayMs: Date.now() - pos.timestamp,
        accuracy: Math.round(pos.accuracy),
        subscriptions
    });
    return true;
}
